﻿import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import type { ApiError, EmployeePenalty } from '@pms/shared';
import { penaltyService } from '@/services/penalty.service';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { DatePicker } from '@/components/ui/date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { monthDateRange } from '@/lib/dateFormat';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { ListPagination } from './ListPagination';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  RotateCcw,
  ThumbsDown,
  X,
} from 'lucide-react';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatHours(minutes: number): string {
  if (!minutes || minutes <= 0) return '0h 0m';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatDate(iso: string) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

// ── Status badge ───────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: EmployeePenalty['status'] }) {
  const cfg = {
    pending:  { label: 'Pending',  cls: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700/40' },
    approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700/40' },
    rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-600 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700/40' },
  };
  const { label, cls } = cfg[status] ?? { label: status, cls: 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700' };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cls}`}>
      {label}
    </span>
  );
}

function SourceBadge({ source }: { source: 'auto' | 'manual' }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${
      source === 'manual'
        ? 'border-violet-200 bg-violet-100 text-violet-700 dark:border-violet-700/40 dark:bg-violet-900/30 dark:text-violet-300'
        : 'border-border bg-muted text-muted-foreground'
    }`}>
      {source === 'manual' ? 'Manual' : 'Auto'}
    </span>
  );
}

// ── Reject dialog ──────────────────────────────────────────────────────────────

function RejectDialog({
  penalty,
  onConfirm,
  onCancel,
  submitting,
}: {
  penalty: EmployeePenalty;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  submitting: boolean;
}) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-modal-nested flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-popover shadow-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-foreground">Reject Penalty</h3>
          <button onClick={onCancel} className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground">
          Rejecting penalty for{' '}
          <strong className="text-foreground">{[penalty.first_name, penalty.last_name].filter(Boolean).join(' ')}</strong> on{' '}
          {formatDate(penalty.attendance_date)}.
        </p>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Rejection Reason <span className="text-destructive">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter reason for rejection..."
            rows={3}
            className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring transition-colors"
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={submitting}>Cancel</Button>
          <Button
            size="sm"
            disabled={submitting || !reason.trim()}
            className="bg-rose-500 hover:bg-rose-600 text-white gap-1.5 dark:bg-rose-600 dark:hover:bg-rose-500"
            onClick={() => onConfirm(reason.trim())}
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Reject
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main Tab ───────────────────────────────────────────────────────────────────

type StatusFilter = 'pending' | 'all' | 'approved' | 'rejected';

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'pending',  label: 'Pending' },
  { value: 'all',      label: 'All' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export function PenaltyApprovalTab() {
  const qc = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [search,       setSearch]       = useState('');
  const [page,         setPage]         = useState(1);
  const [pageSize,     setPageSize]     = useState(20);
  // Penalty-date filters: a single date OR a month — picking one clears the other
  const [filterDate,   setFilterDate]   = useState('');
  const [filterMonth,  setFilterMonth]  = useState<number | ''>('');
  const [filterYear,   setFilterYear]   = useState<number>(new Date().getFullYear());
  const [rejectTarget, setRejectTarget] = useState<EmployeePenalty | null>(null);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const monthRange = filterMonth !== '' ? monthDateRange(filterYear, filterMonth) : undefined;
  const dateFrom = filterDate || monthRange?.from;
  const dateTo   = filterDate || monthRange?.to;
  const hasPeriodFilter = !!dateFrom;

  const clearPeriod = () => { setFilterDate(''); setFilterMonth(''); setPage(1); };

  const debouncedSearch = useDebouncedValue(search.trim());

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['penalties', 'tab', statusFilter, debouncedSearch, page, pageSize, dateFrom, dateTo],
    queryFn: () =>
      penaltyService.list({
        status:    statusFilter === 'all' ? undefined : statusFilter,
        q:         debouncedSearch || undefined,
        page,
        limit:     pageSize,
        date_from: dateFrom,
        date_to:   dateTo,
      }),
  });

  const records    = data?.data ?? [];
  const pagination = data?.pagination;

  // An approve/reject can shrink the list under the current page — step back to the last real page
  const totalPages = pagination?.totalPages ?? 0;
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const goToPage = (p: number) => {
    setPage(p);
    rootRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  function invalidateAll() {
    qc.invalidateQueries({ queryKey: ['penalties'] });
    qc.invalidateQueries({ queryKey: ['payroll', 'salary'] });
    qc.invalidateQueries({ queryKey: ['payroll', 'salary', 'me'] });
  }

  const approveMutation = useMutation({
    mutationFn: (id: string) => penaltyService.approve(id),
    onSuccess: () => { invalidateAll(); toast.success('Penalty approved'); },
    onError: (err: unknown) => {
      const msg = (err as AxiosError<ApiError>)?.response?.data?.error?.message ?? 'Failed to approve';
      toast.error(msg);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      penaltyService.reject(id, reason),
    onSuccess: () => {
      invalidateAll();
      setRejectTarget(null);
      setRejectSubmitting(false);
      toast.success('Penalty rejected');
    },
    onError: (err: unknown) => {
      const msg = (err as AxiosError<ApiError>)?.response?.data?.error?.message ?? 'Failed to reject';
      toast.error(msg);
      setRejectSubmitting(false);
    },
  });

  const pendingMutation = useMutation({
    mutationFn: (id: string) => penaltyService.moveToPending(id),
    onSuccess: () => { invalidateAll(); toast.success('Moved back to pending'); },
    onError: (err: unknown) => {
      const msg = (err as AxiosError<ApiError>)?.response?.data?.error?.message ?? 'Failed to update';
      toast.error(msg);
    },
  });

  function handleRejectConfirm(reason: string) {
    if (!rejectTarget) return;
    setRejectSubmitting(true);
    rejectMutation.mutate({ id: rejectTarget.id, reason });
  }

  return (
    <div ref={rootRef} className="scroll-mt-4 space-y-4">
      {/* Section title */}
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <h2 className="text-base font-semibold text-foreground">Penalty Approvals</h2>
        {!isLoading && (
          <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {pagination?.total ?? 0}
          </span>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <SearchInput
            value={search}
            onChange={(v) => { setSearch(v); setPage(1); }}
            placeholder="Search by name or code…"
            containerClassName="w-full sm:w-64"
          />
          <DatePicker
            value={filterDate}
            onChange={(v) => { setFilterDate(v); if (v) setFilterMonth(''); setPage(1); }}
            placeholder="Penalty date"
            className="h-9 w-44 text-sm"
            modal={false}
          />
          <Select
            value={filterMonth === '' ? 'all' : String(filterMonth)}
            onValueChange={(v) => {
              setFilterMonth(v === 'all' ? '' : Number(v));
              if (v !== 'all') setFilterDate('');
              setPage(1);
            }}
          >
            <SelectTrigger className="h-9 w-36 text-sm">
              <SelectValue placeholder="All Months" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {MONTHS.map((m, i) => (
                <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={String(filterYear)}
            onValueChange={(v) => { setFilterYear(Number(v)); setPage(1); }}
          >
            <SelectTrigger className="h-9 w-24 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[new Date().getFullYear() - 2, new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {filterMonth !== '' && (
            <Button variant="outline" size="sm" className="h-9 px-2.5" onClick={() => { setFilterMonth(''); setPage(1); }}>
              ✕
            </Button>
          )}
        </div>

        {/* Status filter */}
        <div className="flex items-center overflow-hidden rounded-lg border border-border/60 bg-card text-sm">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => { setStatusFilter(f.value); setPage(1); }}
              className={`px-3 py-1.5 font-medium transition-colors ${
                statusFilter === f.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 py-8 text-sm text-destructive">
          <span>Failed to load penalties</span>
          <Button size="sm" variant="outline" onClick={() => void refetch()}>Retry</Button>
        </div>
      ) : records.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border py-12 text-center">
          <AlertTriangle className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            {search.trim()
              ? 'No penalties match your search'
              : hasPeriodFilter
                ? 'No penalties in this period'
                : `No ${statusFilter === 'all' ? '' : statusFilter} penalty records found`}
          </p>
          {search.trim() ? (
            <button onClick={() => setSearch('')} className="mt-2 text-xs text-primary hover:underline">
              Clear search
            </button>
          ) : hasPeriodFilter && (
            <button onClick={clearPeriod} className="mt-2 text-xs text-primary hover:underline">
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rounded-xl border border-border/60 bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/30">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Employee</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Penalty</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {records.map((p) => {
                  const name = [p.first_name, p.last_name].filter(Boolean).join(' ') || '—';
                  return (
                    <tr key={p.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground">{name}</p>
                        {p.employee_code && <p className="text-xs text-muted-foreground">{p.employee_code}</p>}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{formatDate(p.attendance_date)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-rose-600 dark:text-rose-400">{formatHours(p.penalty_minutes)}</span>
                          <SourceBadge source={p.source} />
                        </div>
                      </td>
                      <td className="px-4 py-3 max-w-[200px]">
                        <div className="flex items-start gap-1.5">
                          <FileText className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground/60" />
                          <p className="truncate text-muted-foreground text-xs" title={p.reason}>{p.reason}</p>
                        </div>
                        {p.rejection_reason && (
                          <p className="truncate text-xs text-destructive mt-0.5" title={p.rejection_reason}>
                            ↳ {p.rejection_reason}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          {p.status === 'pending' && (
                            <>
                              <Button size="sm" variant="outline" className="h-7 gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700/40 dark:text-emerald-300 dark:hover:bg-emerald-900/20"
                                onClick={() => approveMutation.mutate(p.id)} disabled={approveMutation.isPending}>
                                <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 gap-1 border-red-200 text-red-600 hover:bg-red-50 dark:border-red-700/40 dark:text-red-400 dark:hover:bg-red-900/20"
                                onClick={() => setRejectTarget(p)}>
                                <ThumbsDown className="h-3.5 w-3.5" /> Reject
                              </Button>
                            </>
                          )}
                          {p.status !== 'pending' && (
                            <Button size="sm" variant="outline" className="h-7 gap-1 text-muted-foreground hover:bg-muted/40"
                              onClick={() => pendingMutation.mutate(p.id)} disabled={pendingMutation.isPending}>
                              <RotateCcw className="h-3.5 w-3.5" /> Revert
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="flex flex-col gap-3 md:hidden">
            {records.map((p) => {
              const name = [p.first_name, p.last_name].filter(Boolean).join(' ') || '—';
              return (
                <div key={p.id} className="rounded-xl border border-border/60 bg-card p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-foreground">{name}</p>
                      {p.employee_code && <p className="text-xs text-muted-foreground">{p.employee_code}</p>}
                    </div>
                    <StatusBadge status={p.status} />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Date</p>
                      <p className="font-medium">{formatDate(p.attendance_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Penalty</p>
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3 w-3 text-rose-500" />
                        <span className="font-semibold text-rose-600 dark:text-rose-400">{formatHours(p.penalty_minutes)}</span>
                        <SourceBadge source={p.source} />
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">{p.reason}</p>
                  {p.rejection_reason && <p className="text-xs text-destructive">↳ {p.rejection_reason}</p>}
                  <div className="flex flex-wrap items-center gap-1.5">
                    {p.status === 'pending' && (
                      <>
                        <Button size="sm" variant="outline" className="h-7 gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => approveMutation.mutate(p.id)} disabled={approveMutation.isPending}>
                          <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 gap-1 border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => setRejectTarget(p)}>
                          <ThumbsDown className="h-3.5 w-3.5" /> Reject
                        </Button>
                      </>
                    )}
                    {p.status !== 'pending' && (
                      <Button size="sm" variant="outline" className="h-7 gap-1 text-muted-foreground hover:bg-muted/40"
                        onClick={() => pendingMutation.mutate(p.id)} disabled={pendingMutation.isPending}>
                        <RotateCcw className="h-3.5 w-3.5" /> Revert
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {pagination && pagination.total > 0 && (
            <ListPagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              pageSize={pageSize}
              onPageChange={goToPage}
              onPageSizeChange={(n) => { setPageSize(n); goToPage(1); }}
            />
          )}
        </>
      )}

      {rejectTarget && (
        <RejectDialog
          penalty={rejectTarget}
          onConfirm={handleRejectConfirm}
          onCancel={() => { setRejectTarget(null); setRejectSubmitting(false); }}
          submitting={rejectSubmitting}
        />
      )}
    </div>
  );
}
